/**
 * FIManagerRow Component
 *
 * Expandable row displaying a single F&I manager's performance.
 * Collapsed: rank, name, back-end gross with PVR, units breakdown.
 * Expanded: Service Contracts and Product Mix detail lines.
 * The Service Contracts line expands again into the per-contract
 * drill-down list (date, deal #, type code, term/miles, coverage).
 */

import React, { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { FiManager } from './types';

interface FIManagerRowProps {
  fiManager: FiManager;
  isExpanded: boolean;
  onToggle: () => void;
  rank: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPvr(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return formatCurrency(value);
}

function formatPct(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}%`;
}

// "2026-06-06" → "6/6/26" (parse parts directly; new Date() on a date-only
// string is UTC and shifts a day in negative-offset timezones)
function formatSaleDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return `${m}/${d}/${String(y).slice(-2)}`;
}

function formatTerm(months?: number, miles?: string): string {
  if (months == null && !miles) return '-';
  return `${months ?? '-'}/${miles ?? '-'}`;
}

export const FIManagerRow: React.FC<FIManagerRowProps> = ({
  fiManager,
  isExpanded,
  onToggle,
  rank,
}) => {
  const [contractsExpanded, setContractsExpanded] = useState(false);
  const reserveMixPct =
    fiManager.productMixPct !== null ? 100 - fiManager.productMixPct : null;
  const contracts = fiManager.serviceContracts || [];
  const hasContractDetail = fiManager.serviceContractCount > 0;

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <div
        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-gray-400 transition-transform duration-200">
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </span>
          <span className="text-xs text-gray-400 font-medium w-5">{rank}</span>
          <span className="font-medium text-gray-900 truncate">
            {fiManager.fiManagerName}
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right">
            <span className="text-lg font-bold text-blue-600">
              {formatCurrency(fiManager.backEndGross)}
            </span>
            <div className="text-xs text-gray-500">
              PVR: {formatPvr(fiManager.pvr)}
            </div>
          </div>
          <div className="text-right text-sm w-44">
            <span className="text-blue-700 font-medium">{fiManager.newUnits}</span>
            <span className="text-gray-400"> new</span>
            <span className="text-gray-400"> / </span>
            <span className="text-gray-700 font-medium">{fiManager.usedUnits}</span>
            <span className="text-gray-400"> used</span>
            <span className="text-gray-400"> = </span>
            <span className="text-gray-900 font-semibold">{fiManager.totalUnits}</span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="ml-12 mr-3 mb-3 bg-gray-50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              <tr
                className={hasContractDetail ? 'hover:bg-white cursor-pointer' : 'hover:bg-white'}
                onClick={(e) => {
                  if (!hasContractDetail) return;
                  e.stopPropagation();
                  setContractsExpanded((open) => !open);
                }}
              >
                <td className="py-2 px-3 text-gray-700">
                  <span className="inline-flex items-center gap-1">
                    {hasContractDetail && (
                      <span className="text-gray-400">
                        {contractsExpanded ? (
                          <ChevronDownIcon className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRightIcon className="h-3.5 w-3.5" />
                        )}
                      </span>
                    )}
                    Service Contracts
                  </span>
                </td>
                <td className="py-2 px-3 text-right text-gray-900 font-medium">
                  {fiManager.totalUnits} units /{' '}
                  <span className={hasContractDetail ? 'text-blue-700 underline decoration-dotted underline-offset-2' : ''}>
                    {fiManager.serviceContractCount} service contracts
                  </span>
                </td>
              </tr>
              {contractsExpanded && hasContractDetail && (
                <tr className="border-t border-gray-200">
                  <td colSpan={2} className="py-2 px-3 bg-white">
                    {contracts.length > 0 ? (
                      <table className="w-full text-xs font-mono">
                        <thead>
                          <tr className="text-gray-400 text-left">
                            <th className="py-1 pr-4 font-medium">Date</th>
                            <th className="py-1 pr-4 font-medium">Deal #</th>
                            <th className="py-1 pr-4 font-medium">Type Code</th>
                            <th className="py-1 pr-4 font-medium">Term/Miles</th>
                            <th className="py-1 font-medium">Coverage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contracts.map((c) => (
                            <tr key={`${c.dealNumber}-${c.typeCode || ''}`} className="text-gray-700">
                              <td className="py-0.5 pr-4 whitespace-nowrap">{formatSaleDate(c.saleDate)}</td>
                              <td className="py-0.5 pr-4">{c.dealNumber}</td>
                              <td className="py-0.5 pr-4">{c.typeCode || '-'}</td>
                              <td className="py-0.5 pr-4">{formatTerm(c.months, c.miles)}</td>
                              <td className="py-0.5">{c.coverageDesc || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <span className="text-xs text-gray-400">
                        Contract detail not available for this month — data was loaded before
                        the drill-down shipped. Use Refresh to reload.
                      </span>
                    )}
                  </td>
                </tr>
              )}
              <tr className="border-t border-gray-200 hover:bg-white">
                <td className="py-2 px-3 text-gray-700">Product Mix</td>
                <td className="py-2 px-3 text-right text-gray-900 font-medium">
                  Products {formatPct(fiManager.productMixPct)} ({formatCurrency(fiManager.productGross)})
                  <span className="text-gray-400"> / </span>
                  Reserve {formatPct(reserveMixPct)} ({formatCurrency(fiManager.financeReserve)})
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
